import { useState } from 'react'
import { Empty, ErrorBanner, useAsync } from '../../components/Async'
import { ConfirmDialog } from '../../components/ConfirmDialog'
import { Explainer } from '../../components/Explainer'
import { ApiError, deployment } from '../../lib/api'

/**
 * How often the fleet is expected to say hello.
 *
 * Check-in is the only thing that carries a rotated credential, a new trust anchor, a published or
 * revoked policy and a capture request, so the number on this card is how long the fleet can be wrong
 * for - and it reads like a preference. That is why slowing it asks and speeding it up does not
 * (D-428).
 *
 * It sits with the credential lifetime rather than under the kits it used to follow. A credential
 * rotates only on a successful check-in, so these two numbers multiply into one answer - how long a
 * machine can be switched off before it is holding nothing this server would accept - and they were
 * on different tabs, each explaining half of it.
 */
export function CheckInCadenceCard() {
  const schedules = useAsync(() => deployment.schedules(), [])

  const [failure, setFailure] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  // Whether the "this slows the fleet down" question is open. See `askOrSave` for when it is asked.
  const [asking, setAsking] = useState(false)

  const [kind, setKind] = useState<'daily-count' | 'fixed-times'>('daily-count')
  const [timesPerDay, setTimesPerDay] = useState(2)
  const [localTimes, setLocalTimes] = useState('09:00, 17:00')
  const [timeZoneId, setTimeZoneId] = useState('UTC')

  /**
   * How many times a day this draft has a device check in.
   *
   * A `fixed-times` list is counted by its entries, which is the same quantity by a different name.
   */
  const perDay = kind === 'daily-count'
    ? timesPerDay
    : localTimes.split(',').filter((value) => value.trim().length > 0).length

  // The schedule with no device group is the one this card edits: the organization's own. Tested for
  // falsiness rather than against null, because the API omits null members and `deviceGroupId` on that
  // row is absent rather than null - which is how this read silently found nothing and asked nothing.
  const currentSchedule = schedules.value?.find((entry) => !entry.deviceGroupId) ?? null
  const currentPerDay = currentSchedule
    ? (currentSchedule.timesPerDay ?? currentSchedule.localTimes.length)
    : null

  /** Asks only when the fleet is being slowed down. Checking in more often reaches nothing later. */
  function askOrSave() {
    if (currentPerDay !== null && perDay > 0 && perDay < currentPerDay) setAsking(true)
    else void saveSchedule()
  }

  async function saveSchedule() {
    setAsking(false)
    setBusy(true)
    setFailure(null)
    setNotice(null)
    try {
      const result = await deployment.setSchedule({
        kind,
        timesPerDay: kind === 'daily-count' ? timesPerDay : undefined,
        localTimes:
          kind === 'fixed-times'
            ? localTimes.split(',').map((value) => value.trim()).filter((value) => value.length > 0)
            : undefined,
        timeZoneId
      })
      schedules.reload()
      setNotice(`Devices will check in at ${result.localTimes.join(', ')} ${timeZoneId}.`)
    } catch (error) {
      setFailure(error instanceof ApiError ? error.message : 'The schedule could not be saved.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="card">
      <div className="card-body">
        <h3 style={{ marginTop: 0 }}>Check-in cadence</h3>
        <p className="muted" style={{ marginTop: 0 }}>
          How often every device polls this server. Times are local to each device, so nine in the
          morning means nine where the machine is.
        </p>

        <ErrorBanner message={failure ?? schedules.error} />
        {notice && (
          <div className="banner success" role="status" data-testid="deployment-notice">
            {notice}
          </div>
        )}

        <div className="toolbar">
          <select
            aria-label="Schedule kind"
            style={{ width: 'auto' }}
            value={kind}
            onChange={(event) => setKind(event.target.value as 'daily-count' | 'fixed-times')}
            data-testid="schedule-kind"
          >
            <option value="daily-count">A number of times a day</option>
            <option value="fixed-times">At fixed local times</option>
          </select>

          {kind === 'daily-count' ? (
            <label className="field" style={{ width: 'auto' }}>
              Times per day
              <input
                type="number"
                min={1}
                max={24}
                value={timesPerDay}
                onChange={(event) => setTimesPerDay(Number(event.target.value))}
                data-testid="schedule-times-per-day"
              />
            </label>
          ) : (
            <input
              className="grow"
              placeholder="09:00, 13:00, 17:00"
              aria-label="Local times"
              value={localTimes}
              onChange={(event) => setLocalTimes(event.target.value)}
              data-testid="schedule-local-times"
            />
          )}

          <input
            placeholder="UTC"
            aria-label="Time zone"
            value={timeZoneId}
            onChange={(event) => setTimeZoneId(event.target.value)}
            data-testid="schedule-time-zone"
            style={{ width: 'auto' }}
          />

          <button
            type="button"
            className="primary"
            onClick={askOrSave}
            disabled={busy}
            data-testid="save-schedule"
          >
            Save
          </button>
        </div>

        <Explainer summary="Why this number is more than a preference" data-testid="cadence-explainer">
          <p className="muted" style={{ marginTop: 0 }}>
            Check-in is what carries a rotated credential, a new trust anchor, a published or revoked
            policy, and a request for a capture. Nothing on this server can make a device ask sooner,
            so this interval is the longest the fleet can be wrong for.
          </p>
          <p className="hint">
            It is also half of how long a machine can be switched off and still come back. A credential
            rotates at a check-in, and the lifetime below decides how much slack there is before the
            old one expires.
          </p>
        </Explainer>
      </div>

      <div className="table-scroll">
        <table data-testid="schedule-table">
          <thead>
            <tr>
              <th>Applies to</th>
              <th>Times</th>
              <th className="secondary">Zone</th>
              <th className="secondary">Retry</th>
            </tr>
          </thead>
          <tbody>
            {schedules.value?.map((schedule) => (
              <tr key={schedule.checkInScheduleId}>
                <td>{schedule.deviceGroupId ? 'A device group' : 'Every device'}</td>
                <td className="mono">{schedule.localTimes.join(', ')}</td>
                <td className="secondary">{schedule.timeZoneId}</td>
                <td className="muted secondary">
                  {schedule.retryUntilSuccess ? `every ${Math.round(schedule.retrySeconds / 60)} min` : 'no'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {schedules.value && schedules.value.length === 0 && (
        <Empty>No schedule set. Devices check in twice a day by default.</Empty>
      )}

      <ConfirmDialog
        open={asking}
        title="Check in less often"
        confirmLabel={`Check in ${perDay === 1 ? 'once' : `${perDay} times`} a day`}
        danger
        busy={busy}
        onConfirm={() => void saveSchedule()}
        onClose={() => setAsking(false)}
        data-testid="schedule-confirm"
      >
        <div className="banner warning" role="note" data-testid="schedule-slower">
          Devices go from {currentPerDay} check-ins a day to {perDay}, so anything that reaches a
          machine only at check-in waits up to {Math.round(24 / Math.max(perDay, 1))} hours instead of{' '}
          {Math.round(24 / Math.max(currentPerDay ?? 1, 1))}.
        </div>

        <p>
          That is every rotated credential, every new trust anchor, every published or revoked policy,
          and every capture request. A device that has not checked in has not been told, and nothing
          on this server can make it ask sooner.
        </p>
      </ConfirmDialog>
    </div>
  )
}
